import { useState, useEffect } from 'react';
import logoImg from '@/assets/logo.png';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  LayoutDashboard, Bot, TrendingUp, Bell, Menu, X, LogOut,
  Wifi, User, Settings, HelpCircle, ShieldAlert, ChevronUp,
  Activity, CheckCircle2, Signal, CircleDot, Wallet, Brain, BookOpen, Store, Briefcase,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/hooks/useNotifications';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { Badge } from '@/components/ui/badge';

interface DashboardLayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onPageChange: (page: string) => void;
  onLogout: () => void;
}

const baseSidebarItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'portfolio', label: 'Portfolio', icon: Briefcase },
  { id: 'market', label: 'Market', icon: TrendingUp },
  { id: 'strategies', label: 'Strategies', icon: Bot },
  { id: 'signals', label: 'AI Signals', icon: Brain },
  { id: 'accounts', label: 'Accounts', icon: Wallet },
  { id: 'brokers', label: 'Brokers', icon: Wifi },
  { id: 'journal', label: 'Journal', icon: BookOpen },
  { id: 'marketplace', label: 'Marketplace', icon: Store },
  { id: 'backtest', label: 'Backtest', icon: Activity },
  { id: 'support', label: 'Support', icon: HelpCircle },
];

// Admin user IDs - configure via VITE_ADMIN_USER_IDS (comma-separated UUIDs) build env var
const ADMIN_USER_IDS = new Set<string>(
  ((import.meta.env.VITE_ADMIN_USER_IDS as string | undefined) ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
);

const DashboardLayout = ({ children, currentPage, onPageChange, onLogout }: DashboardLayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showEmergencyDialog, setShowEmergencyDialog] = useState(false);
  const [isEmergencyStopping, setIsEmergencyStopping] = useState(false);
  const [hasNewSignal, setHasNewSignal] = useState(false);
  const { user } = useAuth();
  const { label: tierLabel, tier } = useSubscription();

  const isAdmin = user?.id ? ADMIN_USER_IDS.has(user.id) : false;
  const sidebarItems = isAdmin
    ? [...baseSidebarItems, { id: 'admin', label: 'Admin', icon: ShieldAlert }]
    : baseSidebarItems;
  const { unreadCount: notifUnreadCount } = useNotifications();

  // Listen for new signal alerts from onboarding
  useEffect(() => {
    const handler = () => setHasNewSignal(true);
    window.addEventListener('newSignalAlert', handler);
    return () => window.removeEventListener('newSignalAlert', handler);
  }, []);

  const tierBadgeColor = tier === 'quant' ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20'
    : tier === 'pro' ? 'bg-primary/15 text-primary border-primary/20'
    : 'bg-muted text-muted-foreground border-border/30';

  const { data: pendingSignalCount = 0 } = useQuery({
    queryKey: ['ai-signals-pending-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('ai_signals')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString());
      if (error) return 0;
      return count ?? 0;
    },
    refetchInterval: 30000,
  });

  const handleEmergencyStop = async () => {
    setIsEmergencyStopping(true);
    try {
      const { data, error } = await supabase.functions.invoke('emergency-stop');
      if (error) throw error;
      toast({
        title: 'Emergency Stop Executed',
        description: `${data.strategies_stopped} strategies stopped, ${data.orders_cancelled} orders cancelled.`,
        variant: 'destructive',
      });
    } catch (err) {
      toast({
        title: 'Emergency Stop Failed',
        description: String(err),
        variant: 'destructive',
      });
    } finally {
      setIsEmergencyStopping(false);
      setShowEmergencyDialog(false);
    }
  };

  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const userInitials = userName.slice(0, 2).toUpperCase();
  const userEmail = user?.email || '';

  return (
    <TooltipProvider delayDuration={200}>
      <div className="min-h-screen bg-background flex flex-col lg:flex-row">
        {/* Mobile top bar */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-3 py-2 bg-background/90 backdrop-blur-xl border-b border-border/20" style={{ paddingTop: 'calc(0.5rem + env(safe-area-inset-top, 0px))' }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsSidebarOpen(true)}
            className="h-9 w-9 p-0 press-scale"
          >
            <Menu className="h-4 w-4" />
          </Button>
          <img src={logoImg} alt="AutoTradePlug logo" className="w-8 h-8 object-contain" />
          <Button
            variant="ghost"
            size="sm"
            className="relative h-9 w-9 p-0 press-scale"
            onClick={() => {
              const event = new CustomEvent('openNotifications');
              window.dispatchEvent(event);
            }}
          >
            <Bell className="w-4 h-4" />
            {notifUnreadCount > 0 && (
            <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-destructive rounded-full flex items-center justify-center">
              <span className="text-[9px] font-medium text-destructive-foreground">{notifUnreadCount > 9 ? '9+' : notifUnreadCount}</span>
            </div>
            )}
          </Button>
        </div>

        {/* Desktop Sidebar Rail */}
        <aside className="hidden lg:flex fixed left-0 top-0 z-40 h-screen w-[72px] bg-card/60 backdrop-blur-xl border-r border-border/20 flex-col items-center">
          {/* Logo */}
          <div className="py-5 w-full flex justify-center">
            <img src={logoImg} alt="AutoTradePlug logo" className="w-9 h-9 object-contain" />
          </div>

          {/* Nav Icons */}
          <nav className="flex-1 flex flex-col items-center gap-1 py-3">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.id;
              return (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onPageChange(item.id)}
                      className={`relative w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                        isActive
                          ? 'bg-primary text-primary-foreground shadow-[0_0_12px_hsl(var(--primary)/0.2)]'
                          : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                      }`}
                    >
                      <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                      {item.id === 'signals' && pendingSignalCount > 0 && (
                        <div className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-destructive rounded-full flex items-center justify-center">
                          <span className="text-[9px] font-bold text-destructive-foreground">{pendingSignalCount > 9 ? '9+' : pendingSignalCount}</span>
                        </div>
                      )}
                      {item.id === 'signals' && hasNewSignal && pendingSignalCount === 0 && (
                        <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[hsl(var(--accent-purple))] rounded-full animate-pulse" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={12} className="bg-popover border-border/50 text-xs">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </nav>

          {/* Profile */}
          <div className="pb-5 flex flex-col items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex flex-col items-center gap-1 group cursor-pointer">
                  <Avatar className="w-8 h-8 border-2 border-border/40 group-hover:border-primary/40 transition-colors">
                    <AvatarFallback className="bg-muted text-[10px] font-medium text-muted-foreground">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <Badge variant="outline" className={`text-[8px] px-1.5 py-0 h-4 ${tierBadgeColor}`}>
                    {tierLabel}
                  </Badge>
                  <ChevronUp className="w-3 h-3 text-muted-foreground/50 group-hover:text-foreground transition-colors" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="end" sideOffset={12} className="w-64 bg-popover border-border/50">
                {/* User identity */}
                <div className="px-3 py-2.5 flex items-center gap-2.5">
                  <Avatar className="w-9 h-9 border border-primary/20">
                    <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{userName}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{userEmail}</p>
                  </div>
                  <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(var(--success)/0.6)] flex-shrink-0" />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[9px] uppercase tracking-widest text-muted-foreground/50 px-3 py-1.5">System Status</DropdownMenuLabel>
                <div className="px-3 py-2 mx-2 mb-1.5 rounded-lg bg-muted/15 border border-border/10 space-y-1.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground/70 flex items-center gap-1.5"><CircleDot className="w-3 h-3" strokeWidth={1.75} /> Automation</span>
                    <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full heartbeat-healthy" /><span className="text-[hsl(var(--success)/0.7)] font-medium">Active</span></div>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground/70 flex items-center gap-1.5"><Signal className="w-3 h-3" strokeWidth={1.75} /> Broker</span>
                    <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success)/0.7)]" /><span className="text-[hsl(var(--success)/0.7)] font-medium">Connected</span></div>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground/70">Last Sync</span>
                    <span className="text-muted-foreground/50 tabular-nums">30s ago</span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[9px] uppercase tracking-widest text-muted-foreground/50 px-3 py-1.5">Account</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onPageChange('profile')}><User className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPageChange('settings')}><Settings className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Settings</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPageChange('support')}><HelpCircle className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Support</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[9px] uppercase tracking-widest text-destructive/40 px-3 py-1.5">Danger Zone</DropdownMenuLabel>
                <DropdownMenuItem className="text-[hsl(var(--warning)/0.7)] focus:text-[hsl(var(--warning)/0.7)]" onClick={() => setShowEmergencyDialog(true)}>
                  <ShieldAlert className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Emergency Stop
                  <span className="ml-auto text-[10px] text-muted-foreground/40">all bots</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} className="text-destructive/70 focus:text-destructive/70">
                  <LogOut className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && (
          <div className="fixed inset-0 z-[60] lg:hidden">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-200"
              onClick={() => setIsSidebarOpen(false)}
            />
            {/* Sidebar Panel */}
            <div
              className="absolute left-0 top-0 h-full w-[280px] bg-card border-r border-border/20 flex flex-col animate-slide-in-left"
              style={{ animation: 'slide-in-left 0.2s ease-out forwards' }}
            >
              {/* Close button */}
              <div className="flex items-center justify-between px-4 py-4">
                <div className="flex items-center gap-2.5">
                  <img src={logoImg} alt="AutoTradePlug logo" className="w-8 h-8 object-contain" />
                  <span className="text-sm font-bold font-heading">AutoTradePlug</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setIsSidebarOpen(false)} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Nav Items */}
              <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
                {sidebarItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentPage === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => { onPageChange(item.id); setIsSidebarOpen(false); }}
                      className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 ${
                        isActive
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                      }`}
                    >
                      <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.75} />
                      <span>{item.label}</span>
                      {item.id === 'signals' && pendingSignalCount > 0 && (
                        <div className="ml-auto w-5 h-5 bg-destructive rounded-full flex items-center justify-center">
                          <span className="text-[10px] font-bold text-destructive-foreground">{pendingSignalCount > 9 ? '9+' : pendingSignalCount}</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </nav>

              {/* User section */}
              <div className="border-t border-border/20 px-3 py-3">
                <div className="flex items-center gap-2.5">
                  <Avatar className="w-8 h-8 border border-border/40">
                    <AvatarFallback className="bg-muted text-[10px] font-medium text-muted-foreground">{userInitials}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{userName}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{userEmail}</p>
                  </div>
                  <Badge variant="outline" className={`text-[8px] px-1.5 py-0 h-4 ${tierBadgeColor}`}>{tierLabel}</Badge>
                </div>
                <Button variant="ghost" size="sm" className="w-full mt-2 justify-start gap-2 text-destructive/70 h-8 text-xs" onClick={onLogout}>
                  <LogOut className="h-3.5 w-3.5" /> Sign Out
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 pt-[calc(52px+env(safe-area-inset-top,0px))] pb-[calc(60px+env(safe-area-inset-bottom,0px))] lg:!pt-0 lg:!pb-0 lg:pl-[72px] overflow-y-auto">
          {children}
        </main>

        {/* Mobile Bottom Navigation */}
        {!isSidebarOpen && <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 flex items-end justify-around h-[60px] bg-[hsl(var(--sidebar-background))] border-t border-[hsl(var(--sidebar-border))]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          {[
            { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { id: 'market', label: 'Market', icon: TrendingUp },
            { id: 'strategies', label: 'Strategies', icon: Bot },
            { id: 'signals', label: 'Signals', icon: Brain },
            { id: '_more', label: 'More', icon: Menu },
          ].map((item) => {
            const Icon = item.icon;
            const isActive = item.id !== '_more' && currentPage === item.id;
            const handleClick = () => {
              if (item.id === '_more') {
                setIsSidebarOpen(true);
              } else {
                onPageChange(item.id);
              }
            };
            return (
              <button
                key={item.id}
                onClick={handleClick}
                data-debug-id={item.id === '_more' ? 'bottom-nav-more' : undefined}
                className={`relative flex flex-col items-center justify-center gap-0.5 flex-1 py-2 press-scale ${
                  isActive ? 'text-[hsl(var(--accent-blue))]' : 'text-muted-foreground'
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={1.75} />
                <span className="text-[10px] font-medium">{item.label}</span>
                {item.id === 'signals' && pendingSignalCount > 0 && (
                  <div className="absolute top-1 ml-4 w-3.5 h-3.5 bg-destructive rounded-full flex items-center justify-center">
                    <span className="text-[8px] font-bold text-destructive-foreground">{pendingSignalCount > 9 ? '9+' : pendingSignalCount}</span>
                  </div>
                )}
              </button>
            );
          })}
        </nav>}

        {/* Emergency Stop Confirmation Dialog */}
        <AlertDialog open={showEmergencyDialog} onOpenChange={setShowEmergencyDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <ShieldAlert className="h-5 w-5" /> Emergency Stop
              </AlertDialogTitle>
              <AlertDialogDescription>
                This will immediately <strong>stop ALL strategies</strong> and <strong>cancel all pending orders</strong>. This action cannot be undone. Are you sure you want to continue?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isEmergencyStopping}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleEmergencyStop}
                disabled={isEmergencyStopping}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isEmergencyStopping ? 'Stopping…' : 'Stop All Trading'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};

export default DashboardLayout;
